import {
  Box,
  Button,
  CardMedia,
  Dialog,
  Divider,
  Grid,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Link as RouterLink, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { styled, SxProps, Theme } from '@mui/system';
import { useEffect, useState } from 'react';
import KeyboardArrowRightSharpIcon from '@mui/icons-material/KeyboardArrowRightSharp';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
  APPLICATION_TYPE,
  USER_ROLES,
} from '../../utils/constant';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { setNestedObjectValues, useFormikContext } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import { checkValidationBasedOnDate } from '../../utils/utilityFunctions';
import { useFeatureToggles } from '../../utils/toggler';
import { RequiredFieldsMessage } from '../RequiredFieldsMessage';
import { getPageTitleFromPath } from '../../lib/formik/pageTitle';
import { getSaveActionAriaLabel } from '../../utils/applicationAccessibility';
import { ApplicationSectionHeading } from '../ApplicationSectionHeading';

const menuItemKeyboardFocusSx: SxProps<Theme> = {
  position: 'relative',
  '&:focus-visible, &.Mui-focusVisible': {
    outline: '2px solid #153760',
    outlineOffset: '2px',
    boxShadow: 'none',
    zIndex: 2,
    backgroundColor: '#FAFCFE',
    color: '#367F83',
  },
  '&:focus-visible .MuiListItemText-primary, &.Mui-focusVisible .MuiListItemText-primary': {
    color: '#367F83',
  },

  '&:focus-visible .MuiSvgIcon-root, &.Mui-focusVisible .MuiSvgIcon-root': {
    color: '#367F83',
  },

  '&.Mui-selected:focus-visible, &.Mui-selected.Mui-focusVisible': {
    outline: '2px solid #153760',
    outlineOffset: '2px',
    backgroundColor: '#FAFCFE',
    color: '#367F83',
  },
  '&.Mui-selected:focus-visible .MuiListItemText-primary, &.Mui-selected.Mui-focusVisible .MuiListItemText-primary':
    {
      color: '#367F83',
    },
};

// Investor Details Layout
const MenuItemList = (hasPoa: boolean) => [
  'Distributor details',
  'Contributor Details',
  'Contact Details',
  'Additional KYC Details',
  'FATCA',
  'Nominee Details',
  "Investor's Bank Details",
  'Risk Profiles',
  'Investment & Payment Details',
  // 'Investor Questionnaire',
  'Document Details',
  ...(hasPoa ? ['POA Authorised Signatory'] : []),
];
const HeaderList = (hasPoa: boolean) => [
  'Distributor details',
  'Contributor Details',
  'Contact Details',
  'Additional KYC Details',
  'FATCA',
  'Nominee Details',
  "Investor's Bank Details",
  'Risk Profiles',
  'Investment Details',
  // "Investor Questionnaire",
  'Document Details',
  ...(hasPoa ? ['POA Authorised Signatory'] : []),
];
const MenuItemListLink = (hasPoa: boolean) => [
  'distributor-details',
  'contributor-details',
  'contact-details',
  'additional-KYC-details',
  'fatca',
  'nominee-details',
  'bank-details',
  'risk-profiles',
  'investment-payment-details',
  // "investor-questionnaire",
  'document-details',
  ...(hasPoa ? ['authorised-signatory'] : []),
];

const NonIndividualMenuItemList = (hasPoa: boolean) => [
  'Distributor details',
  'Contributor Details',
  'Contact Details',
  'Investment & Payment Details',
  "Investor's Bank Details",
  'Risk Profiles',
  // 'Investor Questionnaire',
  'Document Details',
  !hasPoa ? 'Authorised Signatory' : 'POA Authorised Signatory',
  'Declaration of UBO',
  'FATCA',
  // 'Risk Profile',
];

const NonIndividualHeaderList = (hasPoa: boolean) => [
  'Distributor details',
  'Contributor Details',
  'Contact Details',
  'Investment Details',
  "Investor's Bank Details",
  'Risk Profiles',
  // "Investor Questionnaire",
  'Document Details',
  !hasPoa ? 'Authorised Signatory' : 'POA Authorised Signatory',
  'Declaration of Ultimate Beneficial Ownership (UBO)',
  'FATCA',
  // 'Risk Profile',
];

const NonIndividualMenuItemListLink = [
  'distributor-details',
  'contributor-details',
  'contact-details',
  'investment-payment-details',
  'bank-details',
  'risk-profiles',
  // "investor-questionnaire",
  'document-details',
  'authorised-signatory',
  'declaration-ubo',
  'fatca',
  // 'risk-profile',
];

export const getSideNavComponent = (
  navOptions: string[],
  createdAt: string,
  nomineeValidationDate: string
): string[] => {
  return checkValidationBasedOnDate(createdAt, nomineeValidationDate)
    ? navOptions.filter((e) => !['nominee-details', 'Nominee Details'].includes(e))
    : navOptions;
};

export const InvestorDetailsLayout = ({
  applicationId,
  applicationNumber = '',
  applicant1ReferenceId = '',
  children,
}: {
  applicationId: string;
  applicationNumber: string;
  applicant1ReferenceId?: string;
  children: JSX.Element;
}): JSX.Element => {
  const { application } = useSelector((store: RootStateType) => store.application);
  const { createdAt = '', hasPOA = false } = application || {};
  const { currentStep } = application || {};
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const dateFeatureToggles = useFeatureToggles();

  const { nomineeValidationDate } = dateFeatureToggles;

  const { path } = useRouteMatch();
  const history = useHistory();
  const [transitionClassName, setTransitionClassName] = useState('');
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const result = useRouteMatch<{ slug: string }>({
    path: `${path}/:slug/`,
  });
  const activeItem = result
    ? application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? getSideNavComponent(
          NonIndividualMenuItemListLink,
          createdAt,
          nomineeValidationDate
        ).indexOf(result.params.slug)
      : getSideNavComponent(MenuItemListLink(hasPOA), createdAt, nomineeValidationDate).indexOf(
          result.params.slug
        )
    : 0;
  const menuList =
    application &&
    (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? getSideNavComponent(NonIndividualMenuItemList(hasPOA), createdAt, nomineeValidationDate)
      : getSideNavComponent(MenuItemList(hasPOA), createdAt, nomineeValidationDate));
  const getMenuItemLink = (index: number): string =>
    `${
      application &&
      (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
        ? getSideNavComponent(NonIndividualMenuItemListLink, createdAt, nomineeValidationDate)[
            index
          ]
        : getSideNavComponent(MenuItemListLink(hasPOA), createdAt, nomineeValidationDate)[index])
    }`;
  useEffect(() => {
    window.scrollTo(0, 0);
    setTransitionClassName('slideInUp');
    const timeoutId = setTimeout(() => {
      setTransitionClassName('');
    }, 300);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [activeItem]);
  return (
    <>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', mb: 2 }}>
        <IconButton
          aria-label="Go back"
          onClick={() =>
            history.push(
              role === USER_ROLES.INVESTOR
                ? `/investment-details/${applicant1ReferenceId}/application-details`
                : [USER_ROLES.AMCAPPROVER, USER_ROLES.POAAPPROVER].includes(role)
                ? `/application-details/${applicationId}`
                : '/applications'
            )
          }>
          <ArrowBackIosRoundedIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 500, fontFamily: 'Poppins, sans-serif' }}>Back</Typography>
      </Box>
      {/* Mobile dropdown */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mx: 0, maxWidth: '100%' }}>
        <Button
          variant="text"
          fullWidth
          id="application-button"
          aria-controls="application-dropdown"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          startIcon={<CardMedia component="img" src="/images/person.svg" alt="Icon" />}
          endIcon={<ExpandMoreIcon />}
          sx={{
            color: 'text.primary',
            bgcolor: '#F7FAFD',
            px: 2,
            justifyContent: 'flex-start',
            textAlign: 'left',
            borderBottom: '1px solid #E8E8E8',
            borderRadius: 0,
            '.MuiButton-endIcon': {
              ml: 'auto',
            },
          }}>
          <Box>
            <Typography variant="body2">
              Page {activeItem + 1}/{' '}
              {application &&
                (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                  ? getSideNavComponent(
                      NonIndividualHeaderList(hasPOA),
                      createdAt,
                      nomineeValidationDate
                    ).length
                  : getSideNavComponent(HeaderList(hasPOA), createdAt, nomineeValidationDate)
                      .length)}
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              {application &&
                (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                  ? getSideNavComponent(
                      NonIndividualHeaderList(hasPOA),
                      createdAt,
                      nomineeValidationDate
                    )[activeItem]
                  : getSideNavComponent(HeaderList(hasPOA), createdAt, nomineeValidationDate)[
                      activeItem
                    ])}
            </Typography>
          </Box>
        </Button>
        <Menu
          id="application-dropdown"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'application-button',
          }}
          sx={{
            '.MuiPaper-root ': {
              width: '100%',
              bgcolor: '#F7FAFD',
              '.MuiList-root': {
                p: 0,
                '.MuiDivider-root': { m: 0 },
                '.MuiMenuItem-root': {
                  ...menuItemKeyboardFocusSx,
                  '.MuiListItemText-primary': {
                    fontWeight: 500,
                    fontSize: 14,
                  },
                  [`:nth-of-type(${activeItem + 1})`]: {
                    '.MuiListItemText-primary': {
                      fontWeight: 600,
                      color: 'primary.main',
                    },
                  },
                },
              },
            },
          }}>
          {menuList &&
            menuList.map((item, index) => {
              const isItemDisabled = !currentStep ? index !== 0 : index > currentStep;
              const menuItemLink = getMenuItemLink(index);
              return (
                <>
                  <MenuItem
                    key={index}
                    selected={activeItem === index && !isItemDisabled}
                    role={isItemDisabled ? undefined : 'none'}
                    sx={{ minHeight: '30px', ...menuItemKeyboardFocusSx }}
                    disabled={isItemDisabled}>
                    {isItemDisabled ? (
                      <ListItemText
                        sx={{
                          color: '#575757',
                          opacity: 0.5,
                        }}>
                        {item}
                      </ListItemText>
                    ) : (
                      <Box
                        component={RouterLink}
                        to={{
                          pathname: menuItemLink,
                          state: {
                            id: applicationId,
                            applicant1ReferenceId,
                          },
                        }}
                        onClick={() => setAnchorEl(null)}
                        aria-current={activeItem === index ? 'page' : undefined}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          textDecoration: 'none',
                          color: 'inherit',
                        }}>
                        <ListItemText
                          sx={{
                            color: currentStep === index ? '#575757' : '#367F83',
                          }}>
                          {item}
                        </ListItemText>
                        <ArrowForwardIcon sx={{ color: '#00D9DA' }} />
                      </Box>
                    )}
                  </MenuItem>
                  <Divider />
                </>
              );
            })}
        </Menu>
      </Box>
      {/* Mobile dropdown End */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '10px 20px',
          mb: 2,
          bgcolor: '#e5f1f8',
          borderRadius: '10px',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            aria-label="Go back"
            sx={{ height: 'fit-content', p: 0 }}
            onClick={() =>
              history.push(
                role === USER_ROLES.INVESTOR
                  ? `/investment-details/${applicant1ReferenceId}/application-details`
                  : [USER_ROLES.AMCAPPROVER, USER_ROLES.POAAPPROVER].includes(role)
                  ? `/application-details/${applicationId}`
                  : '/applications'
              )
            }>
            <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: '#1C2D47',
            }}>
            {application &&
              (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                ? [USER_ROLES.AMCAPPROVER].includes(role)
                  ? 'Edit Application'
                  : 'Non Individual Investor Onboarding'
                : [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER, USER_ROLES.AMCAPPROVER].includes(
                    role
                  )
                ? 'Edit Application'
                : 'Investor Onboarding')}
          </Typography>
        </Box>
        <Typography sx={{ fontWeight: 600, letterSpacing: '1px' }}></Typography>
        {!!applicationNumber && (
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              color: '#1C2D47',
              opacity: 0.7,
            }}>
            Application ID: {applicationNumber}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          bgcolor: 'white',
          boxShadow: { xs: '', md: '0px 4px 12px rgba(0, 0, 0, 0.05)' },
          borderRadius: '10px',
          maxWidth: '100%',
          minWidth: 0,
          overflow: { xs: 'hidden', md: 'visible' },
          width: '100%',
        }}>
        <Box>
          <Grid
            container
            sx={
              {
                // height: { xs: '', md: 'calc(100vh - 200px)' },
                // overflowY: 'scroll',
                // '::-webkit-scrollbar': {
                //   display: 'none',
                // },
              }
            }>
            <Grid
              item
              xs={2}
              sx={{
                display: { xs: 'none', md: 'block' },
                backgroundColor: 'rgba(51, 127, 201, 0.025)',
              }}>
              <Stack sx={{ position: 'sticky', top: '70px', py: 4, overflow: 'visible' }}>
                <MenuList sx={{ overflow: 'visible', py: 0.5 }}>
                  {menuList &&
                    menuList.map((item, index) => {
                      const isItemDisabled = !currentStep ? index !== 0 : index > currentStep;
                      const menuItemLink = getMenuItemLink(index);
                      return (
                        <MenuItem
                          key={index}
                          selected={activeItem === index && !isItemDisabled}
                          role={isItemDisabled ? undefined : 'none'}
                          disabled={isItemDisabled}
                          sx={{
                            ...menuItemKeyboardFocusSx,
                            color: isItemDisabled || currentStep === index ? '#575757' : '#367F83',
                            opacity: isItemDisabled ? 0.5 : 1,
                            fontSize: 14,
                            fontWeight: 500,
                            my: 1,
                            whiteSpace: 'break-spaces',
                            justifyContent: 'flex-end',
                            textAlign: 'right',
                            ':hover': {
                              background:
                                'linear-gradient(88.93deg, rgba(51, 127, 201, 0.0875) 10.58%, rgba(51, 127, 201, 0.025) 93.37%)',
                            },
                            [`:nth-of-type(${activeItem + 1})`]: {
                              fontWeight: 600,
                              color: 'primary.main',
                              background:
                                'linear-gradient(88.93deg, rgba(51, 127, 201, 0.0875) 10.58%, rgba(51, 127, 201, 0.025) 93.37%)',
                              '&:after': {
                                content: '""',
                                position: 'absolute',
                                top: '0',
                                right: '0',
                                width: '4px',
                                height: '100%',
                                bgcolor: 'primary.main',
                                borderRadius: '5px',
                              },
                              '&:focus-visible::after, &.Mui-focusVisible::after': {
                                opacity: 1,
                              },
                            },
                          }}>
                          {isItemDisabled ? (
                            <Typography
                              component="h2"
                              sx={{
                                m: 0,
                                fontSize: 14,
                                fontWeight: 'inherit',
                                color: 'inherit',
                                textAlign: 'right',
                                width: '100%',
                              }}>
                              {item}
                            </Typography>
                          ) : (
                            <Typography
                              component="h2"
                              sx={{
                                m: 0,
                                fontSize: 14,
                                fontWeight: 'inherit',
                                color: 'inherit',
                                textAlign: 'right',
                                width: '100%',
                              }}>
                              <Box
                                component={RouterLink}
                                to={{
                                  pathname: menuItemLink,
                                  state: {
                                    id: applicationId,
                                    applicant1ReferenceId,
                                  },
                                }}
                                aria-current={activeItem === index ? 'page' : undefined}
                                sx={{
                                  width: '100%',
                                  color: 'inherit',
                                  textDecoration: 'none',
                                }}>
                                {item}
                              </Box>
                            </Typography>
                          )}
                        </MenuItem>
                      );
                    })}
                </MenuList>
              </Stack>
            </Grid>
            <Grid item container xs={12} md={10} sx={{ py: 5, maxWidth: '100%' }}>
              <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'block' } }} />
              <Grid item xs={12} md={10} sx={{ maxWidth: '100%' }}>
                <Box
                  sx={{ px: { xs: 1, md: 'unset' }, maxWidth: '100%' }}
                  className={transitionClassName}>
                  {application && (
                    <ApplicationSectionHeading
                      variant="form"
                      title={
                        application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                          ? getSideNavComponent(
                              NonIndividualHeaderList(hasPOA),
                              createdAt,
                              nomineeValidationDate
                            )[activeItem]
                          : getSideNavComponent(HeaderList(hasPOA), createdAt, nomineeValidationDate)[
                              activeItem
                            ]
                      }
                      sx={{
                        color:
                          application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                            ? '#0D2762'
                            : 'secondary.main',
                      }}
                    />
                  )}
                  <RequiredFieldsMessage />
                  {children}
                </Box>
              </Grid>
              <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'block' } }} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};
// Investor Details Layout End

export const SubHeading = styled('h3')(({ theme }) => ({
  backgroundColor: 'rgba(221, 234, 243, 0.34)',
  color: theme.palette.primary.main,
  fontSize: '14px',
  fontWeight: 600,
  borderRadius: '5px',
  width: '100%',
  letterSpacing: '1px',
  // marginLeft: '40px',
  marginTop: '30px',
  padding: '10px 30px',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    padding: '10px 16px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    fontSize: '13px',
  },
  // [theme.breakpoints.down('md')]: {
  //   textAlign: 'center',
  // },
}));

export const ProceedSaveLater = ({
  saveLater,
  saveAndProceed,
  loader,
  clickedButton,
  saveButtonText = 'Save and Proceed',
  saveAndProceedType = 'submit',
  showEndIcon = true,
  showSaveForLater = true,
  disabled,
  stepName: stepNameProp,
}: {
  saveLater?: () => void;
  saveAndProceed?: () => void;
  loader?: boolean;
  clickedButton?: string;
  saveAndProceedType?: 'button' | 'submit' | 'reset' | undefined;
  saveButtonText?: string;
  showEndIcon?: boolean;
  showSaveForLater?: boolean;
  disabled?: boolean;
  stepName?: string | null;
}): JSX.Element => {
  const location = useLocation();
  const stepName = stepNameProp ?? getPageTitleFromPath(location.pathname);
  const saveForLaterAriaLabel = getSaveActionAriaLabel('saveForLater', { stepName });
  const saveAndProceedAriaLabel = getSaveActionAriaLabel('saveAndProceed', {
    stepName,
    proceedButtonText: saveButtonText,
  });
  const formikContext = useFormikContext<any>();
  const validateForm = formikContext?.validateForm;
  const setTouched = formikContext?.setTouched;
  const submitForm = formikContext?.submitForm;
  const getFirstErrorField = (errors: any, parentPath = ''): string => {
    if (!errors || typeof errors !== 'object') return '';
    for (const key of Object.keys(errors)) {
      const value = errors[key];
      const path = parentPath ? `${parentPath}.${key}` : key;
      if (typeof value === 'string') return path;
      const nestedPath = getFirstErrorField(value, path);
      if (nestedPath) return nestedPath;
    }
    return '';
  };
  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        mt: 5,
        display: 'flex',
        flexWrap: 'wrap',
        gap: { xs: 1.5, sm: 3 },
        justifyContent: 'center',
        '.MuiButton-root': {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.5,
          width: { xs: '100%', sm: 220 },
          minWidth: { sm: 220 },
          maxWidth: { xs: '100%', sm: 220 },
          whiteSpace: 'nowrap',
        },
      }}>
      {showSaveForLater && (
        <LoadingButton
          loadingPosition="start"
          type="button"
          aria-label={saveForLaterAriaLabel}
          onClick={async (e) => {
            if (!validateForm || !setTouched || !submitForm) {
              saveLater?.();
              return;
            }
            e.preventDefault();
            saveLater?.();
            const errors = await validateForm();
            if (Object.keys(errors).length) {
              setTouched(setNestedObjectValues(errors, true), true);
              const firstErrorField = getFirstErrorField(errors);
              const firstInvalidElement = document.getElementsByName(firstErrorField)?.[0] as
                | HTMLElement
                | undefined;
              firstInvalidElement?.focus();
              return;
            }
            submitForm();
          }}
          loading={clickedButton === 'save for later' ? loader : false}
          sx={{
            color: 'text.primary',
            border: '1px dashed #AAB2BA',
          }}
          disabled={disabled ? disabled : loader}>
          Save For Later
        </LoadingButton>
      )}
      <LoadingButton
        loadingPosition="start"
        type={saveAndProceedType === 'submit' ? 'button' : saveAndProceedType}
        aria-label={saveAndProceedAriaLabel}
        loading={clickedButton === 'save and proceed' ? loader : false}
        variant="contained"
        onClick={async (e) => {
          if (saveAndProceedType !== 'submit' || !validateForm || !setTouched || !submitForm) {
            saveAndProceed?.();
            return;
          }
          e.preventDefault();
          saveAndProceed?.();
          const errors = await validateForm();
          if (Object.keys(errors).length) {
            setTouched(setNestedObjectValues(errors, true), true);
            const firstErrorField = getFirstErrorField(errors);
            const firstInvalidElement = document.getElementsByName(firstErrorField)?.[0] as
              | HTMLElement
              | undefined;
            firstInvalidElement?.focus();
            return;
          }
          submitForm();
        }}
        endIcon={showEndIcon && <KeyboardArrowRightSharpIcon />}
        disabled={disabled ? disabled : loader}>
        {saveButtonText}
      </LoadingButton>
    </Box>
  );
};

export const Notes = ({
  displayContent,
  displayContent1 = '',
}: {
  displayContent: string;
  displayContent1?: string;
}): JSX.Element => {
  return (
    <Typography
      component="div"
      sx={{
        backgroundColor: 'rgba(51, 127, 201, 0.04)',
        display: 'flex',
        flexDirection: 'row',
        padding: '10px 20px',
        borderRadius: '5px',
        alignItems: 'flex-start',
        mt: 0.5,
        mb: 1,
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <InfoOutlinedIcon color="info" fontSize="small" />
      <Typography
        sx={{
          color: 'rgba(41, 49, 57, 0.7)',
          fontSize: '13px',
          fontWeight: 500,
          ml: 1,
        }}>
        <Typography
          sx={{
            color: 'rgba(41, 49, 57, 0.7)',
            fontSize: '13px',
            fontWeight: 500,
            ml: 1,
          }}>
          {displayContent}
        </Typography>
        {displayContent1 && (
          <Typography
            sx={{
              color: 'rgba(41, 49, 57, 0.7)',
              fontSize: '13px',
              fontWeight: 500,
              ml: 1,
              mt: 0.5,
            }}>
            {displayContent1}
          </Typography>
        )}
      </Typography>
    </Typography>
  );
};

export const InfoDialog = ({
  onClose,
  open,
  content,
}: {
  onClose: any;
  open: any;
  content: string;
}): JSX.Element => {
  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{
        '.MuiPaper-root ': {
          maxWidth: 400,
          p: { xs: 2, sm: '15px 20px 45px' },
          borderRadius: '10px',
          height: 'unset',
          overflowY: 'auto',
        },
      }}>
      <IconButton onClick={onClose} aria-label="Close" sx={{ alignSelf: 'flex-end' }}>
        <CloseIcon fontSize="medium" />
      </IconButton>
      <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
        <InfoOutlinedIcon color="info" fontSize="large" />
      </Box>
      <Box
        sx={{
          width: { xs: '100%', sm: '80%' },
          mx: 'auto',
          '.MuiTypography-root': {
            fontSize: 20,
            fontWeight: 500,
            textAlign: 'center',
          },
        }}>
        <Typography sx={{ color: 'secondary.main', lineHeight: '35px' }}>{content}</Typography>
      </Box>
    </Dialog>
  );
};
